import type { AdHocFilter, SegmentRule } from "@domains/segments/shared/segmentsTypes.ts";
import { Button } from "@/components/ui/button.tsx";
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalTitle } from "@/components/ui/modal.tsx";
import { Note } from "@/components/ui/note.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useSegmentsClient } from "@domains/segments/client/api/segmentsQuery.ts";
import { REPORTING_MANAGER_SYSTEM_ATTRIBUTE_ID } from "@domains/segments/client/components/rules/ruleConditions.ts";
import { SegmentRules } from "@domains/segments/client/components/rules/SegmentRules.tsx";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession, useUserRoles } from "@packages/core-client/session/SessionContext.tsx";
import { ErrorState } from "@packages/core-client/errors/ErrorState.tsx";
import { useDebouncedValue } from "@packages/core-client/hooks/useDebouncedValue.ts";
import { useLoadingIndicator } from "@packages/core-client/hooks/useLoadingIndicator.tsx";
import { useSkeletonState } from "@packages/core-client/hooks/useSkeletonState.ts";
import { useI18nextInstance } from "@squide/i18next";
import { useQuery } from "@tanstack/react-query";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { setTeamAdHocFilter, TEAM_FILTER_MATCH_TYPE, useTeamAdHocDraft } from "../hooks/teamAdHocFilter.ts";
import { useTeamScope } from "../hooks/useTeamScope.ts";
import { useTeamSegment } from "../hooks/useTeamSegment.ts";
import { i18NextInstanceKey } from "../i18n/i18next.ts";
import { SaveAsSegmentDialog } from "./SaveAsSegmentDialog.tsx";

const PLACEHOLDER_FIELDS = { items: [] };

const PREVIEW_DEBOUNCE_MS = 300;

interface FilterRulesProps {
    rules: SegmentRule[];
    onRulesChange: (rules: SegmentRule[]) => void;
    onRequestSaveAsSegment: (rules: SegmentRule[]) => void;
    onApplyFilters: (filter: AdHocFilter, draft: AdHocFilter) => void;
}

function FilterRules({ rules, onRulesChange, onRequestSaveAsSegment, onApplyFilters }: FilterRulesProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("TeamFilterPopover", { i18n: i18nextInstance });
    const session = useSession();
    const { queryUtils } = useSegmentsClient();
    const [scope] = useTeamScope();
    const [segmentId] = useTeamSegment();
    const { isReportingManager, isExecutive, isOrganizationAdmin } = useUserRoles();

    const orgId = session?.organizationId ?? "";

    const {
        data: employeeFields,
        isPending: areFieldsPending,
        isError: haveFieldsFailed,
        refetch: refetchFields
    } = useQuery(
        createQueryOptions(queryUtils.listEmployeeFields.queryOptions({ input: { orgId } }), {
            throwOnError: false
        })
    );

    const { isLoading: areFieldsLoading, hasContent: hasFieldsContent } = useSkeletonState({ isPending: areFieldsPending, data: employeeFields ? [employeeFields] : undefined, placeholder: [PLACEHOLDER_FIELDS] });

    const handleRetryFields = useCallback(() => void refetchFields(), [refetchFields]);

    const [hasIncompleteRule, setHasIncompleteRule] = useState(false);

    const { data: segment, isPending: isSegmentPending } = useQuery(
        createQueryOptions(queryUtils.getSegment.queryOptions({ input: { orgId, segmentId: segmentId ?? "" } }), {
            enabled: !!segmentId && !!orgId,
            throwOnError: false
        })
    );
    const isSegmentActive = !!segmentId && !!segment;

    const isUnrestrictedRole = isExecutive || isOrganizationAdmin;
    const isManagerRestrictedToReportingLine = isReportingManager && !isUnrestrictedRole && scope === "organization";
    const isScopedToReportingLine = !isSegmentActive && (scope !== "organization" || isManagerRestrictedToReportingLine);

    const reportingManagerFieldId = employeeFields?.items.find(field => field.systemAttributeId === REPORTING_MANAGER_SYSTEM_ATTRIBUTE_ID)?.id;
    const viewerMemberId = session?.memberId;
    const lockRules = useMemo((): SegmentRule[] => {
        if (isSegmentActive && segment) {
            return segment.rules;
        }
        if (isScopedToReportingLine && reportingManagerFieldId && viewerMemberId) {
            return [{ propertyId: reportingManagerFieldId, operator: scope === "direct-reports" ? "AnyOf" : "InReportingLineOf", values: [viewerMemberId] }];
        }
        return [];
    }, [isSegmentActive, segment, isScopedToReportingLine, reportingManagerFieldId, viewerMemberId, scope]);

    const debouncedRules = useDebouncedValue(rules, PREVIEW_DEBOUNCE_MS);
    const combinedRules = useMemo(() => [...lockRules, ...debouncedRules], [lockRules, debouncedRules]);
    const liveCombinedRules = useMemo(() => [...lockRules, ...rules], [lockRules, rules]);

    const handleSaveAsSegment = useCallback(() => onRequestSaveAsSegment(liveCombinedRules), [onRequestSaveAsSegment, liveCombinedRules]);

    const handleApplyFilters = useCallback(() => onApplyFilters({ matchType: TEAM_FILTER_MATCH_TYPE, rules: liveCombinedRules }, { matchType: TEAM_FILTER_MATCH_TYPE, rules }), [onApplyFilters, liveCombinedRules, rules]);

    const previewInput = useMemo(() => ({ orgId, matchType: TEAM_FILTER_MATCH_TYPE, rules: combinedRules, pageSize: 1 }), [orgId, combinedRules]);

    const { data: preview, isPending: isPreviewPending } = useQuery(
        createQueryOptions(queryUtils.previewSegmentMembers.queryOptions({ input: previewInput }), {
            enabled: combinedRules.length > 0,
            throwOnError: false
        })
    );

    const isCountPending = combinedRules.length > 0 && isPreviewPending;
    const isCountLoading = useLoadingIndicator(isCountPending || (isSegmentActive && isSegmentPending));
    const totalCount = preview?.totalCount;
    const matchCount = totalCount !== undefined ? t("matchCount", { count: totalCount }) : "";

    const isActionDisabled = rules.length === 0 || hasIncompleteRule;

    return (
        <>
            <ModalBody className="flex flex-col gap-3">
                <output className="sr-only">{areFieldsLoading && t("loading")}</output>

                {haveFieldsFailed ? (
                    <ErrorState isEmbedded kind="load" title={t("error")} onRetry={handleRetryFields} retryLabel={t("retry")} />
                ) : !hasFieldsContent ? null : areFieldsLoading || !employeeFields ? (
                    <Skeleton isLoading={areFieldsLoading} inert={areFieldsLoading} className="flex flex-col gap-2">
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center gap-2">
                                <div className="h-4 w-12" />
                                <div className="h-9 w-24" />
                            </div>
                            <div className="flex flex-col gap-2 sm:flex-row">
                                <div className="h-9 w-full sm:w-48" />
                                <div className="h-9 w-full sm:w-56" />
                                <div className="h-9 w-full sm:flex-1" />
                            </div>
                        </div>
                    </Skeleton>
                ) : (
                    <SegmentRules
                        layout="compact"
                        fields={employeeFields.items}
                        rules={rules}
                        hasRequiredRules={isSegmentActive && !!segment && segment.requiredRules.length > 0}
                        hasUnlistedRules={isSegmentActive && (segment?.hasUnlistedRules ?? false)}
                        hasUnlistedRequiredRules={isSegmentActive && (segment?.hasUnlistedRequiredRules ?? false)}
                        lockedRules={lockRules}
                        matchType={TEAM_FILTER_MATCH_TYPE}
                        isMatchTypeFixed
                        matchTypeFixedReason={t("matchTypeFixed")}
                        isOwnedByViewer={!isSegmentActive || !segment || segment.isEditable}
                        ownerDisplayName={segment?.ownerDisplayName}
                        shouldShowErrors={hasIncompleteRule}
                        onRulesChange={onRulesChange}
                        onValidityChange={setHasIncompleteRule}
                    />
                )}
            </ModalBody>
            <ModalFooter className="sm:justify-between">
                <output className="sr-only">{isCountLoading && t("countLoading")}</output>
                <Skeleton isLoading={isCountLoading} inert={isCountLoading} className="flex">
                    <div>
                        <div>{isCountLoading ? <div className="h-4 w-24" /> : <p className="text-muted-fg text-sm">{matchCount}</p>}</div>
                    </div>
                </Skeleton>
                <div className="flex gap-2">
                    <Button intent="outline" isDisabled={isActionDisabled} name="save-as-segment" onPress={handleSaveAsSegment}>
                        {t("saveAsSegment")}
                    </Button>
                    <Button intent="primary" isDisabled={isActionDisabled} name="apply-filters" onPress={handleApplyFilters}>
                        {t("applyFilters")}
                    </Button>
                </div>
            </ModalFooter>
        </>
    );
}

interface TeamFilterPopoverModalContentProps {
    isMatchAnySegmentActive: boolean;
    isSavingAsSegment: boolean;
    onSavedAsSegment: (segmentId: string) => void;
    onIsSavingChange: (isSaving: boolean) => void;
    onApplyFilters: (filter: AdHocFilter, draft: AdHocFilter) => void;
}

function TeamFilterPopoverModalContent({ isMatchAnySegmentActive, isSavingAsSegment, onSavedAsSegment, onIsSavingChange, onApplyFilters }: TeamFilterPopoverModalContentProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("TeamFilterPopover", { i18n: i18nextInstance });
    const appliedDraft = useTeamAdHocDraft();
    const [rules, setRules] = useState<SegmentRule[]>(appliedDraft?.rules ?? []);
    const [saveAsSegmentRules, setSaveAsSegmentRules] = useState<SegmentRule[] | undefined>(undefined);

    const handleRequestSaveAsSegment = useCallback((requestRules: SegmentRule[]) => setSaveAsSegmentRules(requestRules), []);
    const handleBackToFilters = useCallback(() => setSaveAsSegmentRules(undefined), []);

    return (
        <ModalContent size="3xl" isDismissable={!isSavingAsSegment} isKeyboardDismissDisabled={isSavingAsSegment}>
            {saveAsSegmentRules ? (
                <SaveAsSegmentDialog matchType={TEAM_FILTER_MATCH_TYPE} rules={saveAsSegmentRules} onBack={handleBackToFilters} onSaved={onSavedAsSegment} onIsSavingChange={onIsSavingChange} />
            ) : (
                <>
                    <ModalHeader>
                        <ModalTitle className="text-base/6">{t("dialogTitle")}</ModalTitle>
                    </ModalHeader>
                    {isMatchAnySegmentActive ? (
                        <ModalBody>
                            <Note intent="warning">
                                <p className="text-sm">{t("matchAnySegment")}</p>
                            </Note>
                        </ModalBody>
                    ) : (
                        <FilterRules rules={rules} onRulesChange={setRules} onRequestSaveAsSegment={handleRequestSaveAsSegment} onApplyFilters={onApplyFilters} />
                    )}
                </>
            )}
        </ModalContent>
    );
}

export interface TeamFilterPopoverHandle {
    open: () => void;
}

export const TeamFilterPopover = forwardRef<TeamFilterPopoverHandle>(function TeamFilterPopover(_props, ref) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("TeamFilterPopover", { i18n: i18nextInstance });
    const session = useSession();
    const { queryUtils } = useSegmentsClient();
    const [scope] = useTeamScope();
    const [segmentId, setSegmentId] = useTeamSegment();
    const appliedDraft = useTeamAdHocDraft();
    const orgId = session?.organizationId ?? "";
    const [isOpen, setIsOpen] = useState(false);
    const [closeCount, setCloseCount] = useState(0);

    const { data: segment } = useQuery(
        createQueryOptions(queryUtils.getSegment.queryOptions({ input: { orgId, segmentId: segmentId ?? "" } }), {
            enabled: !!segmentId && !!orgId,
            throwOnError: false
        })
    );
    const isHandPickedSegmentActive = !!segmentId && !!segment && !segment.isRuleBased;
    const isMatchAnySegmentActive = !!segmentId && !!segment && segment.isRuleBased && segment.matchType === "any";

    useImperativeHandle(ref, () => ({ open: () => !isHandPickedSegmentActive && setIsOpen(true) }), [isHandPickedSegmentActive]);

    const [isSavingAsSegment, setIsSavingAsSegment] = useState(false);
    const handleOpenChange = useCallback((nowOpen: boolean) => {
        if (!nowOpen) {
            setCloseCount(count => count + 1);
        }
        setIsOpen(nowOpen);
    }, []);

    const handleSavedAsSegment = useCallback(
        (newSegmentId: string) => {
            void setSegmentId(newSegmentId);
            setCloseCount(count => count + 1);
            setIsOpen(false);
        },
        [setSegmentId]
    );

    const handleApplyFilters = useCallback((filter: AdHocFilter, draft: AdHocFilter) => {
        setTeamAdHocFilter({ filter, draft });
        setCloseCount(count => count + 1);
        setIsOpen(false);
    }, []);

    return (
        <Modal isOpen={isOpen} onOpenChange={handleOpenChange}>
            {/* intent-ui-deviation: no Button size reaches h-11 (button.tsx tops out at sm:min-h-9) -- overridden
                to match the h-11 MenuTrigger/ToggleGroup it sits beside in TeamScopeSelector. */}
            <Button intent="outline" className="h-11" isDisabled={isHandPickedSegmentActive}>
                <PlusIcon aria-hidden="true" />
                {t("trigger")}
            </Button>
            <TeamFilterPopoverModalContent
                key={`${scope}:${segmentId ?? ""}:${closeCount}:${appliedDraft ? JSON.stringify(appliedDraft) : "none"}`}
                isMatchAnySegmentActive={isMatchAnySegmentActive}
                isSavingAsSegment={isSavingAsSegment}
                onSavedAsSegment={handleSavedAsSegment}
                onIsSavingChange={setIsSavingAsSegment}
                onApplyFilters={handleApplyFilters}
            />
        </Modal>
    );
});
